import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { Message } from 'src/app/shared/models/domain';
import { Notification } from 'src/app/shared/models/insight';
import { CommonService } from 'src/app/shared/services/common.service';
import { InsightService } from 'src/app/shared/services/insight.service';

@Component({
  selector: 'app-reminder',
  templateUrl: './reminder.component.html',
  styleUrls: ['./reminder.component.scss']
})
export class ReminderComponent implements OnInit {
  public isLoading: boolean = true;
  public notifications: Notification[] = [];
  public unseenNotificationIds: number[] = [];

  constructor(
    private insightService: InsightService,
    private commonService: CommonService,
    private toaster: ToastrService
  ) {}

  ngOnInit(): void {
    this.getNotifications();
  }

  // Fetch notifications from the server
  private getNotifications() {
    this.insightService.getNotifications().subscribe({
      next: (notifications: Notification[]) => {
        // ✅ Fix: Properly get unseen notification IDs
        this.unseenNotificationIds = notifications
          .filter(notification => !notification.seen)
          .map(notification => notification.id);

        // Sort: unseen first, then latest on top
        this.notifications = notifications.sort((a, b) => {
          const aSeen = !a.seen ? 0 : 1;
          const bSeen = !b.seen ? 0 : 1;
          if (aSeen !== bSeen) {
            return aSeen - bSeen;
          }
          return b.id - a.id; // latest first
        });

        this.isLoading = false;
      },
      error: (error: HttpErrorResponse) => {
        this.isLoading = false;
        this.commonService.setToastr(500, error?.error?.name || error.error);
      }
    });
  }

  // Mark all unseen notifications as read
  public markAllAsRead() {
    if (this.unseenNotificationIds && this.unseenNotificationIds.length > 0) {
      this.updateNotification(this.unseenNotificationIds);
    }
  }

  // Mark a specific notification as read
  public markAsSeen(notificationId: number) {
    if (!this.unseenNotificationIds.includes(notificationId)) {
      return;
    }

    this.updateNotification([notificationId]);

    // Locally update UI for responsiveness
    this.notifications = this.notifications.map(notification =>
      notification.id === notificationId ? { ...notification, seen: true } : notification
    );

    this.unseenNotificationIds = this.unseenNotificationIds.filter(id => id !== notificationId);
  }

  // Update notification status via API
  private updateNotification(notificationIds: number[]) {
    this.insightService.updateNotification(notificationIds).subscribe({
      next: (message: Message) => {
        this.getNotifications(); // Refresh after update
        if (message.text) {
          this.commonService.setToastr(200, 'Notification marked as read!');
        } else {
          this.commonService.setToastr(500, message.text);
        }
      },
      error: (error: HttpErrorResponse) => {
        this.isLoading = false;
        this.commonService.setToastr(500, error?.error?.name || error.error);
      }
    });
  }

  // Get notification icon based on type
  public getNotificationImage(type: string): string {
    switch (type) {
      case 'LEAVE':
        return 'assets/images/icons/leave.png';
      case 'ABSENCE':
        return 'assets/images/icons/abse.png';
      case 'REGULARIZATION':
        return 'assets/images/icons/regu.png';
      case 'INSURANCE':
        return 'assets/images/icons/insurance.png';
      case 'SALARY':
        return 'assets/images/icons/salary.png';
      case 'RESIGNATION':
        return 'assets/images/icons/res-lett.png';
      default:
        return 'assets/images/avatar.png'; // fallback image
    }
  }

  // Parse routing path and query parameters
  public parseAction(action: string | null | undefined) {
    if (!action) {
      return { path: '', queryParams: {} };
    }

    const [path, queryString] = action.split('?');
    const queryParams = queryString ? this.parseQueryParams(queryString) : {};

    return {
      path: path,
      queryParams: queryParams
    };
  }

  // Parse URL query string to an object
  public parseQueryParams(queryString: string) {
    return queryString.split('&').reduce((params, param) => {
      const [key, value] = param.split('=');
      params[key] = value;
      return params;
    }, {} as { [key: string]: string });
  }
}
